import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PageAction } from '../../../components/page-actions/page-actions.component';
import { Models } from '@ibexfusion/models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ScreenService } from '@services/screens/screen.service';
import { RedirectService } from '@services/redirects/redirect.service';
import { JsonService } from '@services/jsons/json.service';
import { ScreenSettingsService } from '@services/screen-settings/screen-settings.service';
import { GeneratorDialogComponent } from '../../../dialogs/generator-dialog/generator-dialog.component';
import { CodeSnippetService } from '@services/code-snippets/code-snippet.service';

import * as _ from 'lodash';

@Component({
    selector: 'app-screen-detail',
    templateUrl: './screen-detail.component.html',
    styleUrls: ['./screen-detail.component.scss'],
    providers: [ScreenService, RedirectService, JsonService, ScreenSettingsService, CodeSnippetService],
    standalone: false
})
export class ScreenDetailComponent implements OnInit {

  public cf = null;
  public params: any;
  public pageActions: PageAction[];
  public screen: Models.Screen;
  public redirects: Models.Redirect[];
  public title;
  public urlBase;
  public isLoading: boolean;
  public jsonDataProviderParams: any;
  public jsonDataTableColumns = [
    { prop: 'language' }
  ];
  public redirectDataTableColumns = [
    { prop: 'title' }
  ];
  private existingJsonsByLang = {};
  private baseJsonsByName = {};

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private screenService: ScreenService,
    private redirectService: RedirectService,
    public jsonService: JsonService,
    private snippetService: CodeSnippetService,
    private dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.createPageActions();
    this.isLoading = true;

    return this.screenService.get(this.route.snapshot.params['id']).then(response => {
      this.screen = response;
      this.jsonDataProviderParams = {
        screenUUID: this.screen.uuid
      };
      this.jsonService.find(0, 100, this.jsonDataProviderParams).then(res => {
        for (const json of res.data) {
          if (json.language === 'cf') {
            this.cf = JSON.parse(json.data);
          } else {
            this.existingJsonsByLang[json.language] = json;
          }
        }
      });
      this.title = 'Screen: ' + this.screen.outputName;
      this.urlBase = 'https://my.ibexfusion.com/app/launch/' + this.screen.uuid;
      this.redirectService.find(0, 100, { screenUUID: this.screen.uuid }).then(responseRedirect => {
        this.redirects = responseRedirect.data;
      });
      this.isLoading = false;
    });
  }

  navigateToRedirect(event: any) {
    this.router.navigate(['redirects', event.row.getId()]);
  }

  navigateToJsonEdit(event: any) {
    this.router.navigate(['jsons', event.row.getId(), 'edit']);
  }

  navigateToJsonDetail(event: any) {
    this.router.navigate(['jsons', event.row.getId(), 'detail']);
  }

  generateCustomJsons() {
    const dialogRef = this.dialog.open(GeneratorDialogComponent, <MatDialogConfig>{
      width: '330px',
      height: '200px'
    });
    dialogRef.afterClosed().toPromise().then(res => {
      if (res) {
        this.cf.season = res;
        const codeSnippetNames = [];
        for (const lang of this.cf.languages) {
          codeSnippetNames.push(this.cf[lang].baseJSONName);
        }
        this.snippetService.find(0, this.cf.languages.length, {
          name: { $in: codeSnippetNames }
        }).then(jsons => {
          for (const json of jsons.data) {
            this.baseJsonsByName[json.name] = JSON.parse(json.code);
          }
          const generatedJsons = this.generate();
          for (const key in generatedJsons) {
            if (this.existingJsonsByLang[key]) {
              const jsonData = _.clone(this.existingJsonsByLang[key]);
              jsonData.data = JSON.stringify(generatedJsons[key]);
              this.jsonService.update(this.existingJsonsByLang[key]._id, jsonData);
            } else {
              const data = new Models.FakeJSON;
              data.populateFromJSON({
                data: JSON.stringify(generatedJsons[key]),
                language: key,
                screenUUID: this.screen.uuid
              });
              this.jsonService.create(data);
            }
          }
        });
      }
    });
  }

  generate() {
    const config = this.cf;
    if (config.season) {
      for (const lang of config.languages) {
        for (const key in config[lang].config) {
          if (config[lang].config.hasOwnProperty(key)) {
            config[lang].config[key] = _.remove(config[lang].config[key], (element: any) => {
              return element.season ? element.season === config.season : true;
            });
          }
        }
      }
      for (const key in config.general) {
        if (config.general.hasOwnProperty(key)) {
          config.general[key] = _.remove(config.general[key], (element: any) => {
            return element.season ? element.season === config.season : true;
          });
        }
      }
    }
    const jsons = {};
    for (const lang of config.languages) {
      if (config[lang] && config[lang].config) {
        jsons[lang] = this.applyConfig(config[lang].config, this.baseJsonsByName[config[lang].baseJSONName]);
      }
    }

    for (const key in jsons) {
      if (jsons.hasOwnProperty(key)) {
        jsons[key] = this.applyConfig(config.general, jsons[key]);
      }
    }

    for (const lang of config.languages) {
      if (config[lang] && config[lang].config) {
        if (config[lang].config.textOverwrites) {
          for (const item of config[lang].config.textOverwrites) {
            jsons[lang] = JSON.parse(_.replace(JSON.stringify(jsons[lang]), new RegExp(item.old.replace(/\$/g, '\\\$'), 'g'), item.new));
          }
        }
      }
    }

    return jsons;
  }

  applyConfig(config, json) {
    if (config.extends) {
      json = this.extend(config.extends, json);
    }
    if (config.order) {
      json = this.reorder(config.order, json);
    }
    if (config.overwrites) {
      json = this.overwrite(config.overwrites, json);
    }
    if (config.textOverwrites) {
      for (const item of config.textOverwrites) {
        json = JSON.parse(_.replace(JSON.stringify(json), new RegExp(item.old.replace(/\$/g, '\\\$'), 'g'), item.new));
      }
    }
    if (config.removes) {
      json = this.remove(config.removes, json);
    }
    if (config.additionalFields) {
      json = this.addFields(config.additionalFields, json);
    }
    return json;
  }

  extend(config, json) {
    for (const item of config) {
      if (item.location === 'entries') {
        json.entries[item.id] = item.element;
      } else if (item.location === 'items') {
        if (item.key) {
          if (item.position) {
            json.menu.items[item.key]['children'].splice(item.position, 0, item.element);
          } else { // default: push it at the end
            json.menu.items[item.key]['children'].push(item.element);
          }
        } else {
          json.menu.items[item.id] = item.element;
        }
      } else if (item.location === 'files') {
        json.files[item.id] = item.element;
      }
    }
    return json;
  }

  reorder(config, json) {
    for (const item of config) {
      if (item.id) {
        if (json.entries && json.entries[item.id] && json.entries[item.id]) {
          if (item.config) {
            json.entries[item.id].related = this.moveTo(item.config.key, item.config.value, json.entries[item.id].related, item.config.newPosition);
          }
        } else if (json.menu && json.menu.items && json.menu.items[item.id] && json.menu.items[item.id].children) {
          if (item.config) {
            json.menu.items[item.id].children = this.moveTo(item.config.key, item.config.value, json.menu.items[item.id].children, item.config.newPosition);
          }
        }
      }
    }
    return json;
  }

  overwrite(config, json) {
    for (const item of config) {
      if (item.id && item.element) {
        if (json.entries[item.id]) {
          json.entries[item.id] = item.element;
        } else if (json.files[item.id]) {
          json.files[item.id] = item.element;
        } else if (json.menu && json.menu.items[item.id]) {
          json.menu.items[item.id] = item.element;
        }
      }
    }
    return json;
  }

  remove(config, json) {
    for (const item of config) {
      delete json.entries[item.id];
      delete json.files[item.id];
      delete json.menu.items[item.id];
    }
    return json;
  }

  moveTo(key, value, array, position: any = 0) {
    if (_.isString(position)) {
      position = position === 'last' ? array.length - 1 : 0;
    }
    for (let i = 0; i < array.length; i++) {
      if (array[i][key] && array[i][key] === value) {
        const item = _.clone(array[i]);
        array.splice(i, 1);
        array.splice(position, 0, item);
      }
    }
    return array;
  }

  private createPageActions(): void {
    this.pageActions = [
      new PageAction({
        icon: 'info',
        title: 'Overview Customer',
        callback: () => {
          this.router.navigate(['customers', this.screen.customerId]);
        }
      }),
      new PageAction({
        icon: 'settings',
        title: 'Open Screen Settings',
        callback: () => {
          this.router.navigate(['screen-settings', this.screen.uuid]);
        }
      }),
      new PageAction({
        icon: 'transform',
        title: 'Add Redirect',
        callback: () => {
          this.router.navigate(['redirects/create', this.screen.uuid]);
        }
      }),
      new PageAction({
        icon: 'receipt',
        title: 'Device Logs',
        callback: () => {
          this.router.navigate(['device-health/', this.screen.uuid]);
        }
      }),
      new PageAction({
        icon: 'edit',
        title: 'Edit Screen Settings',
        callback: () => {
          this.router.navigate(['screen-settings', this.screen.uuid, 'edit']);
        }
      }),
      new PageAction({
        icon: 'add',
        title: 'Add Custom JSON',
        callback: () => {
          this.router.navigate(['jsons', this.screen.uuid]);
        }
      })
    ];
  }

  private addFields(config, json) {
    for (const item of config) {
      if (item.location === 'entries') {
        json.entries[item.id] = this.addFieldNested(json.entries[item.id], item.field, item.value);
      }
      if (item.location === 'items') {
        json.menu.items[item.id] = this.addFieldNested(json.menu.items[item.id], item.field, item.value);
      }
      if (item.location === 'files') {
        json.files[item.id] = this.addFieldNested(json.files[item.id], item.field, item.value);
      }
    }
    return json;
  }

  private addFieldNested(obj, path, value) {
    const a = path.split('.');
    let o = obj;
    while (a.length - 1) {
      const n = a.shift();
      if (!(n in o)) {
        o[n] = {};
      }
      o = o[n];
    }
    o[a[0]] = value;
    return obj;
  }

}
